/**
 * Random Picker module for whiteboard
 * Randomly select a name from a list
 */

let names = [];
let isSpinning = false;
let spinInterval = null;

// LocalStorage key for persisting names
const STORAGE_KEY = 'whiteboard_picker_names';

/**
 * Initialize random picker widget
 */
export function initRandomPicker() {
    createPickerWidget();
    setupEventListeners();
    loadNamesFromStorage();
    updateNameList();
}

/**
 * Create the picker widget HTML
 */
function createPickerWidget() {
    // Prevent duplicate widget creation
    if (document.getElementById('picker-widget')) {
        return;
    }
    
    const widget = document.createElement('div');
    widget.id = 'picker-widget';
    widget.className = 'picker-widget';
    widget.innerHTML = `
        <div class="picker-header">
            <span class="picker-drag-handle">⋮⋮</span>
            <span class="picker-title">隨機抽籤</span>
            <button class="picker-close-btn" title="關閉">×</button>
        </div>
        <div class="picker-result">
            <span class="picker-result-text">點擊抽籤</span>
        </div>
        <div class="picker-controls">
            <button class="picker-btn picker-spin-btn" title="抽籤">
                <svg viewBox="0 0 24 24" width="20" height="20">
                    <path fill="currentColor" d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                </svg>
                <span>抽籤</span>
            </button>
        </div>
        <div class="picker-names-section">
            <div class="picker-names-header">
                <span>名單 (<span class="picker-count">0</span>人)</span>
                <button class="picker-clear-btn" title="清空名單">清空</button>
            </div>
            <div class="picker-input-row">
                <input type="text" class="picker-name-input" placeholder="輸入名字..." />
                <button class="picker-add-btn" title="新增">+</button>
            </div>
            <div class="picker-batch-input">
                <textarea class="picker-batch-textarea" placeholder="批次新增（每行一個名字）"></textarea>
                <button class="picker-batch-btn">批次新增</button>
            </div>
            <div class="picker-name-list"></div>
        </div>
    `;
    document.body.appendChild(widget);
    
    // Make draggable
    makeDraggable(widget);
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
    const widget = document.getElementById('picker-widget');
    if (!widget) return;
    
    // Close button
    widget.querySelector('.picker-close-btn').addEventListener('click', hidePicker);
    
    // Spin button
    widget.querySelector('.picker-spin-btn').addEventListener('click', spinPicker);
    
    // Add name button
    widget.querySelector('.picker-add-btn').addEventListener('click', addNameFromInput);
    
    // Enter key to add name
    widget.querySelector('.picker-name-input').addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
            addNameFromInput();
        }
    });
    
    // Batch add
    widget.querySelector('.picker-batch-btn').addEventListener('click', addBatchNames);
    
    // Clear all
    widget.querySelector('.picker-clear-btn').addEventListener('click', clearAllNames);
}

/**
 * Make element draggable
 */
function makeDraggable(element) {
    const handle = element.querySelector('.picker-drag-handle');
    let isDragging = false;
    let offsetX, offsetY;
    
    handle.addEventListener('mousedown', (e) => {
        isDragging = true;
        offsetX = e.clientX - element.offsetLeft;
        offsetY = e.clientY - element.offsetTop;
        element.style.cursor = 'grabbing';
    });
    
    document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        element.style.left = (e.clientX - offsetX) + 'px';
        element.style.top = (e.clientY - offsetY) + 'px';
        element.style.right = 'auto';
    });
    
    document.addEventListener('mouseup', () => {
        isDragging = false;
        element.style.cursor = '';
    });
}

/**
 * Add name from input
 */
function addNameFromInput() {
    const widget = document.getElementById('picker-widget');
    const input = widget.querySelector('.picker-name-input');
    const name = input.value.trim();
    
    if (name) {
        addName(name);
        input.value = '';
        input.focus();
    }
}

/**
 * Add batch names from textarea
 */
function addBatchNames() {
    const widget = document.getElementById('picker-widget');
    const textarea = widget.querySelector('.picker-batch-textarea');
    const lines = textarea.value.split('\n');
    
    let added = 0;
    lines.forEach(line => {
        const name = line.trim();
        if (name) {
            names.push(name);
            added++;
        }
    });
    
    if (added > 0) {
        textarea.value = '';
        saveNamesToStorage();
        updateNameList();
    }
}

/**
 * Add a single name
 */
function addName(name) {
    names.push(name);
    saveNamesToStorage();
    updateNameList();
}

/**
 * Remove a name by index
 */
function removeName(index) {
    names.splice(index, 1);
    saveNamesToStorage();
    updateNameList();
}

/**
 * Clear all names
 */
function clearAllNames() {
    if (names.length === 0) return;
    
    if (confirm('確定要清空所有名單嗎？')) {
        names = [];
        saveNamesToStorage();
        updateNameList();
        updateResult('點擊抽籤');
    }
}

/**
 * Update the name list display
 */
function updateNameList() {
    const widget = document.getElementById('picker-widget');
    const listContainer = widget.querySelector('.picker-name-list');
    const countSpan = widget.querySelector('.picker-count');
    
    countSpan.textContent = names.length;
    
    if (names.length === 0) {
        listContainer.innerHTML = '<div class="picker-empty">尚無名單</div>';
        return;
    }
    
    listContainer.innerHTML = names.map((name, index) => `
        <div class="picker-name-item">
            <span class="picker-name-text">${escapeHtml(name)}</span>
            <button class="picker-remove-btn" data-index="${index}" title="移除">×</button>
        </div>
    `).join('');
    
    // Add remove listeners
    listContainer.querySelectorAll('.picker-remove-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const index = parseInt(btn.dataset.index);
            removeName(index);
        });
    });
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Update result display
 */
function updateResult(text, isHighlight = false) {
    const widget = document.getElementById('picker-widget');
    const resultText = widget.querySelector('.picker-result-text');
    const resultBox = widget.querySelector('.picker-result');
    
    resultText.textContent = text;
    
    if (isHighlight) {
        resultBox.classList.add('picker-result-highlight');
    } else {
        resultBox.classList.remove('picker-result-highlight');
    }
}

/**
 * Spin the picker with animation
 */
function spinPicker() {
    if (isSpinning) return;
    if (names.length === 0) {
        updateResult('請先新增名單');
        return;
    }
    
    isSpinning = true;
    const widget = document.getElementById('picker-widget');
    const resultBox = widget.querySelector('.picker-result');
    const spinBtn = widget.querySelector('.picker-spin-btn');
    
    spinBtn.disabled = true;
    resultBox.classList.remove('picker-result-highlight');
    resultBox.classList.add('picker-spinning');
    
    // Spinning animation - show random names quickly
    let spinCount = 0;
    const maxSpins = 20 + Math.floor(Math.random() * 10);
    let spinDelay = 50;
    
    const doSpin = () => {
        const randomIndex = Math.floor(Math.random() * names.length);
        updateResult(names[randomIndex]);
        spinCount++;
        
        // Slow down gradually
        if (spinCount < maxSpins * 0.5) {
            spinDelay = 50;
        } else if (spinCount < maxSpins * 0.7) {
            spinDelay = 100;
        } else if (spinCount < maxSpins * 0.85) {
            spinDelay = 150;
        } else {
            spinDelay = 250;
        }
        
        if (spinCount < maxSpins) {
            spinInterval = setTimeout(doSpin, spinDelay);
        } else {
            // Final result
            const finalIndex = Math.floor(Math.random() * names.length);
            const winner = names[finalIndex];
            
            resultBox.classList.remove('picker-spinning');
            updateResult(winner, true);
            
            // Play sound
            playWinSound();
            
            // Confetti effect
            showConfetti(widget);
            
            isSpinning = false;
            spinBtn.disabled = false;
        }
    };
    
    doSpin();
}

/**
 * Play win sound
 */
function playWinSound() {
    try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        
        // Play a happy chord
        const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
        notes.forEach((freq, i) => {
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.value = freq;
            oscillator.type = 'sine';
            
            const startTime = audioContext.currentTime + i * 0.05;
            gainNode.gain.setValueAtTime(0.3, startTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.5);
            
            oscillator.start(startTime);
            oscillator.stop(startTime + 0.5);
        });
    } catch (e) {
        console.log('Audio not supported');
    }
}

/**
 * Show confetti effect
 */
function showConfetti(widget) {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7', '#a8e6cf'];
    const confettiCount = 30;
    
    for (let i = 0; i < confettiCount; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'picker-confetti';
        confetti.style.left = Math.random() * 100 + '%';
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.animationDelay = Math.random() * 0.5 + 's';
        confetti.style.animationDuration = (Math.random() * 1 + 1) + 's';
        
        widget.querySelector('.picker-result').appendChild(confetti);
        
        // Remove after animation
        setTimeout(() => confetti.remove(), 2000);
    }
}

/**
 * Save names to localStorage
 */
function saveNamesToStorage() {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(names));
    } catch (e) {
        console.log('Could not save to localStorage');
    }
}

/**
 * Load names from localStorage
 */
function loadNamesFromStorage() {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            names = JSON.parse(saved);
        }
    } catch (e) {
        console.log('Could not load from localStorage');
    }
}

/**
 * Show picker widget
 */
export function showPicker() {
    const widget = document.getElementById('picker-widget');
    if (widget) {
        widget.style.display = 'flex';
    }
}

/**
 * Hide picker widget
 */
export function hidePicker() {
    const widget = document.getElementById('picker-widget');
    if (widget) {
        widget.style.display = 'none';
    }
}

/**
 * Toggle picker visibility
 */
export function togglePicker() {
    const widget = document.getElementById('picker-widget');
    if (!widget) return;
    
    // Use getComputedStyle to check actual visibility (handles CSS-set display:none)
    const computedStyle = window.getComputedStyle(widget);
    if (computedStyle.display !== 'none') {
        hidePicker();
    } else {
        showPicker();
    }
}

/**
 * Check if picker is visible
 */
export function isPickerVisible() {
    const widget = document.getElementById('picker-widget');
    if (!widget) return false;
    
    const computedStyle = window.getComputedStyle(widget);
    return computedStyle.display !== 'none';
}
